/**
 * Engine-hosted post-purchase pages for paid programs.
 *
 * Routes:
 *   /programs/:slug/thanks      → ProgramThanksPage      (Spiffy redirects here)
 *   /programs/:slug/onboarding  → ProgramOnboardingPage
 *   /programs/:slug/summary     → ProgramSummaryPage
 *
 * URL params arrive from Spiffy's auto-appended Thank You URL params (per
 * 2021 sample: order=144737&name_first=Michael&name_last=Palmer&email=...
 * &total=110&stripe_cus=cus_...&code=ADMIN, plus address fields). We read
 * the ones we need; the rest survive as cruft in the bar but don't break
 * anything. Subsequent step links preserve order + name + email so the
 * buyer sees a consistent greeting all the way through.
 *
 * Auth model — token-first, Spiffy-params fallback:
 *   1. ?token= (32-hex portal token) → verify via GET /api/programs/:slug/verify-token → identity
 *   2. Spiffy params (?order, ?name_first, ?email) → used directly (immediate post-purchase path)
 *   3. Neither → show RequestAccessForm (buyer emails themselves a fresh token link)
 *   Tokens are issued by the enroll_in_program fulfillment step and embedded in D-7/D-2 emails.
 *
 * Flow context:
 *   The Spiffy webhook (worker/src/spiffy.ts) has independently created a
 *   registration + granted Komunily access by the time the buyer's browser
 *   lands here, so each page can render immediately even if it can't yet
 *   fetch the buyer's full record.
 *
 * The visual layer mirrors ProgramSalesPage (same palette tokens) so the
 * brand carries through from sale → onboarding → registered.
 */

// ────────────────────────────────────────────────────────────────────────────
// Spiffy URL params helper — Spiffy's Thank-You-URL convention
//
// Per the 2021 sample, Spiffy appends these to the redirect URL:
//   order, name_first, name_last, email, total, stripe_cus, code,
//   street1, city, state, zip, country
// We pull what we need + a `getStepQuery()` helper to forward them onward
// without losing the buyer context between thanks → onboarding → summary.
// ────────────────────────────────────────────────────────────────────────────

interface SpiffyUrlParams {
  order: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  total: string | null;
}

function readSpiffyParams(params: URLSearchParams): SpiffyUrlParams {
  // Accept either `order` (Spiffy convention) or `order_id` (legacy test fixtures).
  const order = params.get('order') ?? params.get('order_id');
  return {
    order,
    firstName: params.get('name_first') ?? params.get('fn'),
    lastName: params.get('name_last') ?? params.get('ln'),
    email: params.get('email') ?? params.get('em'),
    total: params.get('total'),
  };
}

function buildStepQuery(p: SpiffyUrlParams, token?: string | null): string {
  // Token is the preferred identity carrier — cleaner URL, works without Spiffy params.
  if (token) return `?token=${encodeURIComponent(token)}`;
  const out = new URLSearchParams();
  if (p.order) out.set('order', p.order);
  if (p.firstName) out.set('name_first', p.firstName);
  if (p.lastName) out.set('name_last', p.lastName);
  if (p.email) out.set('email', p.email);
  const qs = out.toString();
  return qs ? `?${qs}` : '';
}

// ────────────────────────────────────────────────────────────────────────────
// Portal identity hook — token-first, Spiffy-params fallback
//
// On mount: if ?token= present, calls GET /api/programs/:slug/verify-token.
// Resolves to the buyer's email + name regardless of which path they arrive from.
// Falls through to Spiffy URL params when token is absent or doesn't resolve.
// ────────────────────────────────────────────────────────────────────────────

interface PortalIdentity {
  loading: boolean;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  token: string | null;
}

function usePortalIdentity(slug: string, params: URLSearchParams): PortalIdentity {
  const token = params.get('token');
  const spiffy = readSpiffyParams(params);

  const [state, setState] = useState<PortalIdentity>({
    loading: !!token,
    email: token ? null : spiffy.email,
    firstName: token ? null : spiffy.firstName,
    lastName: token ? null : spiffy.lastName,
    token,
  });
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    // vid: a signed sign-in ties this browser to the person (worker visitor-ties.ts).
    const vid = trackableVisitorId();
    fetch(`${API}/api/programs/${encodeURIComponent(slug)}/verify-token?token=${encodeURIComponent(token)}${vid ? `&vid=${encodeURIComponent(vid)}` : ''}`)
      .then((r) => r.json() as Promise<{ ok: boolean; email?: string; firstName?: string; lastName?: string }>)
      .then((data) => {
        setState({
          loading: false,
          email: data.ok && data.email ? data.email : spiffy.email,
          firstName: data.ok && data.firstName ? data.firstName : spiffy.firstName,
          lastName: data.ok && data.lastName ? data.lastName : spiffy.lastName,
          token,
        });
      })
      .catch(() => {
        setState({ loading: false, email: spiffy.email, firstName: spiffy.firstName, lastName: spiffy.lastName, token });
      });
  }, [slug, token]);  // eslint-disable-line react-hooks/exhaustive-deps

  return state;
}

// ────────────────────────────────────────────────────────────────────────────
// Kickoff-for-email hook — resolves the CALLING BUYER'S OWN cohort kickoff
// date/time (GET /api/programs/:slug/kickoff-for-email?email=), not the
// program's active cohort. Fixes /thanks and /summary showing a hardcoded
// kickoff date that goes stale the moment that cohort's kickoff passes
// (confirmed live 2026-07-25: Clients was 3 days past, Freedom Gateway was
// 3 weeks past). Fires once an email is known, from either identity source
// (token-resolved or Spiffy params) — most first-load /thanks pageviews
// have no token yet (Spiffy redirects straight here; tokens are embedded in
// later D-7/D-2 emails), so this can't wait on the token path alone.
// ────────────────────────────────────────────────────────────────────────────

interface KickoffInfo {
  loading: boolean;
  cohortName: string | null;
  kickoffDateLongYear: string | null;
  kickoffTimeRange: string | null;
  kickoffDateTimeFull: string | null;
}

function useKickoffForEmail(slug: string, email: string | null): KickoffInfo {
  const [state, setState] = useState<KickoffInfo>({
    loading: !!email,
    cohortName: null,
    kickoffDateLongYear: null,
    kickoffTimeRange: null,
    kickoffDateTimeFull: null,
  });
  const attempted = useRef<string | null>(null);

  useEffect(() => {
    if (!email || attempted.current === email) return;
    attempted.current = email;
    setState((prev) => ({ ...prev, loading: true }));
    fetch(`${API}/api/programs/${encodeURIComponent(slug)}/kickoff-for-email?email=${encodeURIComponent(email)}`)
      .then((r) => r.json() as Promise<{
        ok: boolean;
        cohortName?: string | null;
        kickoffDateLongYear?: string | null;
        kickoffTimeRange?: string | null;
        kickoffDateTimeFull?: string | null;
      }>)
      .then((data) => {
        setState({
          loading: false,
          cohortName: data.ok ? data.cohortName ?? null : null,
          kickoffDateLongYear: data.ok ? data.kickoffDateLongYear ?? null : null,
          kickoffTimeRange: data.ok ? data.kickoffTimeRange ?? null : null,
          kickoffDateTimeFull: data.ok ? data.kickoffDateTimeFull ?? null : null,
        });
      })
      .catch(() => {
        setState({ loading: false, cohortName: null, kickoffDateLongYear: null, kickoffTimeRange: null, kickoffDateTimeFull: null });
      });
  }, [slug, email]);

  return state;
}

// ────────────────────────────────────────────────────────────────────────────
// Email-fallback form — shown when buyer has no token or Spiffy params
// (e.g. they bookmarked the raw URL). POSTs to /request-access which emails
// them a fresh token link. Always shows success so we can't oracle enrollees.
// ────────────────────────────────────────────────────────────────────────────

function RequestAccessForm({ slug }: { slug: string }) {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    await fetch(`${API}/api/programs/${encodeURIComponent(slug)}/request-access`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase() }),
    }).catch(() => null);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div style={{ ...summaryBlock, borderColor: '#D1FAE5' }}>
        <p style={{ margin: 0, fontSize: '16px', lineHeight: 1.6 }}>
          If that email is on file, we've sent you a link. Check your inbox and click it to get back
          in — it usually arrives within a minute.
        </p>
      </div>
    );
  }

  return (
    <div style={{ ...summaryBlock, marginTop: '8px' }}>
      <p style={{ ...lede, marginBottom: '16px' }}>
        Enter the email you used when you purchased and we'll send you a sign-in link.
      </p>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <input
          type="email"
          required
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={input}
        />
        <button type="submit" disabled={submitting || !email.trim()} style={ctaBtn}>
          {submitting ? 'Sending…' : 'Email me a link →'}
        </button>
      </form>
    </div>
  );
}

import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { BRAND_NAME } from './branding';
import { useBrandGtm } from './lib/useBrandGtm';
import { MarketingOptIn } from './components/MarketingOptIn';
import { trackableVisitorId } from './attribution';
import { NativeBookingPicker } from './components/NativeBookingPicker';

const API = import.meta.env.VITE_API_BASE_URL as string;

// Brand logo for the Clients program — sourced from `brand.logoUrl` on
// /api/public/programs/clients. Hardcoded here so post-purchase pages
// don't need to fetch brand data. R2 URL is stable.
const CLIENTS_PROGRAM_LOGO_URL =
  'https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/assets/programs/clients/logo-light.svg';

// TSB master-brand logo — used by the SODA Intensive post-purchase pages, which
// carry the TSB v2 brand rather than a program-specific logo. R2 URL is stable.
const TSB_BRAND_LOGO_URL =
  'https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/brands/tsb/logo-light.svg';

// Pure Bookkeeping brand logo (light variant for the white top-nav) — used by the
// BKOS License /thanks page, which is PB-branded (TSB resells it).
const PB_BRAND_LOGO_URL =
  'https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/brands/pb/logo-light.svg';
// Where a new BKOS licensee logs in after their seat is provisioned.
const BKOS_LOGIN_URL = 'https://bkos.io';

// The SODA Intensive 75-min 1:1 with Ron Bester books on the Engine's own page
// (ron-bester/soda-intensive) — moving every calendar off HubSpot, Michael
// 28 Oct 2026 (mct_70c7197c-1a4). The HubSpot scheduler stays only as the
// fallback the picker drops to if that page is ever switched off.
const SODA_BOOKING_KEY = 'soda-intensive';
const SODA_BOOKING_URL = 'https://meetings.hubspot.com/ronbester/soda-meet-with-ron';

// ────────────────────────────────────────────────────────────────────────────
// Shared layout chrome
// ────────────────────────────────────────────────────────────────────────────

function PageChrome(props: {
  children: React.ReactNode;
  eyebrow: string;
  slug?: string;
  logoUrl?: string;
  logoAlt?: string;
}) {
  const slug = props.slug ?? 'clients';
  const logoUrl = props.logoUrl ?? CLIENTS_PROGRAM_LOGO_URL;
  // SODA and BKOS have no /lets-talk discovery page — hide the link for them.
  const showTalk = slug !== 'soda-intensive' && slug !== 'bkos-license';
  return (
    <div style={pageWrap}>
      <header style={topNav}>
        <div style={topNavInner}>
          <a href="https://www.thesuccessfulbookkeeper.com" style={topNavLogoLink}>
            <img src={logoUrl} alt={props.logoAlt ?? 'The Successful Bookkeeper — Clients'} style={topNavLogo} />
          </a>
          {showTalk && (
            <a href={`/lets-talk/${slug}`} style={topNavLink}>
              Talk with our team
            </a>
          )}
        </div>
      </header>
      <main style={mainWrap}>
        <p style={eyebrow}>{props.eyebrow}</p>
        {props.children}
      </main>
    </div>
  );
}

function NextSteps(props: { slug: string; spiffy: SpiffyUrlParams; token?: string | null; kickoff: KickoffInfo }) {
  const qs = buildStepQuery(props.spiffy, props.token);
  const { kickoffDateTimeFull } = props.kickoff;
  return (
    <div style={nextStepsCard}>
      <p style={nextStepsHead}>What happens next</p>
      <ol style={nextStepsList}>
        <li>
          <strong>Tell us a bit about your business</strong> so Lisa can tailor the kickoff —
          takes about 2 minutes.
          <div style={{ marginTop: 10 }}>
            <Link to={`/programs/${props.slug}/onboarding${qs}`} style={nextLink}>
              Start onboarding →
            </Link>
          </div>
        </li>
        <li>
          <strong>Check your inbox</strong> for the welcome email with your Komunily login
          {kickoffDateTimeFull
            ? <> + the calendar invite for <strong>{kickoffDateTimeFull}</strong>.</>
            : '.'}
        </li>
        <li>
          <strong>Show up live</strong> the first week.
          {kickoffDateTimeFull
            ? <> We kick off <strong>{kickoffDateTimeFull}</strong> with a newbie breakout — see you there.</>
            : ' Your exact kickoff date and time are in your welcome email — see you there.'}
        </li>
      </ol>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// HubSpot meeting scheduler embed — used by the SODA Intensive /thanks page so
// the buyer books their 75-min session immediately after payment.
//
// We render the HubSpot meeting page directly in an <iframe> with `?embed=true`
// rather than using HubSpot's MeetingsEmbedCode.js loader. The loader binds its
// DOM scan to the page load event, which has already fired by the time React
// injects it post-mount in this SPA — so it silently never renders. A direct
// iframe is immune to that timing bug; the loader's only added value is
// postMessage auto-resize, which we trade for a tall fixed container. Booker
// email/name prefill the scheduler form when known.
// ────────────────────────────────────────────────────────────────────────────

function HubSpotMeetingEmbed(props: {
  baseUrl: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
}) {
  const src = (() => {
    const u = new URL(props.baseUrl);
    u.searchParams.set('embed', 'true');
    if (props.email) u.searchParams.set('email', props.email);
    if (props.firstName) u.searchParams.set('firstName', props.firstName);
    if (props.lastName) u.searchParams.set('lastName', props.lastName);
    return u.toString();
  })();

  return (
    <div style={meetingEmbedWrap}>
      <iframe
        src={src}
        title="Book your SODA Intensive session"
        style={meetingIframe}
        loading="lazy"
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// /thanks — confirmation page right after Spiffy redirect
// ────────────────────────────────────────────────────────────────────────────

export function ProgramThanksPage() {
  const { slug = 'clients' } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  // Brand-aware GTM — the thanks page is the buy-journey conversion pageview.
  // No brand fetch here; the host fallback resolves the container (covers the
  // PB-hosted bkos-license thanks page too). Onboarding/summary pages stay
  // untracked by design — they're post-purchase utility, not funnel steps.
  useBrandGtm();
  const spiffy = readSpiffyParams(params);
  const identity = usePortalIdentity(slug, params);
  const isSoda = slug === 'soda-intensive';
  const isBkos = slug === 'bkos-license';
  const isFg = slug === 'freedom-gateway';

  useEffect(() => {
    document.title = isSoda
      ? `You're booked — SODA Intensive · ${BRAND_NAME}`
      : isBkos
        ? `You're in — BKOS License · ${BRAND_NAME}`
        : isFg
          ? `You're in — Freedom Gateway · ${BRAND_NAME}`
          : `You're in — Clients Program · ${BRAND_NAME}`;
    return () => {
      document.title = BRAND_NAME;
    };
  }, [isSoda, isBkos, isFg]);

  const firstName = identity.firstName ?? spiffy.firstName;
  const email = identity.email ?? spiffy.email;
  const order = spiffy.order;
  const greeting = firstName ? `You're in, ${firstName}.` : "You're in.";
  // Resolved once `email` is known from either identity source (token or
  // Spiffy params) — see useKickoffForEmail's doc comment for why this
  // can't wait on the token path alone.
  const kickoff = useKickoffForEmail(slug, email);
  const [sodaNative, setSodaNative] = useState(true);

  // SODA Intensive — pay-then-book. Confirm payment, then drop the buyer
  // straight into Ron's calendar to pick their 75-min session.
  if (isSoda) {
    return (
      <PageChrome
        eyebrow="Payment confirmed"
        slug={slug}
        logoUrl={TSB_BRAND_LOGO_URL}
        logoAlt="The Successful Bookkeeper"
      >
        <h1 style={h1}>{firstName ? `You're all set, ${firstName}.` : "You're all set."} Now pick your time.</h1>
        <p style={lede}>
          Your SODA Intensive is paid for. One step left: choose the time for your
          75-minute, two-part session with <strong>Ron Bester</strong> using the calendar below.
          You'll get a confirmation and a calendar invite the moment you book.
        </p>
        {sodaNative ? (
          <NativeBookingPicker
            personSlug="ron-bester"
            bookingKey={SODA_BOOKING_KEY}
            prefill={{ email: email ?? undefined, firstName: firstName ?? undefined, lastName: (identity.lastName ?? spiffy.lastName) ?? undefined }}
            onFallback={() => setSodaNative(false)}
          />
        ) : (
          <HubSpotMeetingEmbed
            baseUrl={SODA_BOOKING_URL}
            email={email}
            firstName={firstName}
            lastName={identity.lastName ?? spiffy.lastName}
          />
        )}
        <p style={smallNote}>
          Order reference: <code style={code}>{order ?? '—'}</code>
          {email ? <> · sent to <code style={code}>{email}</code></> : null}. Can't see the
          calendar, or need to reschedule? Reply to your receipt email and we'll sort it.
        </p>
      </PageChrome>
    );
  }

  // BKOS License — firm-level subscription. Confirm payment, then point them to
  // bkos.io. (Phase 1: the seat/login email is manual; Phase 2 auto-provisions on
  // the Spiffy webhook and sends the welcome.)
  if (isBkos) {
    return (
      <PageChrome
        eyebrow="Payment confirmed"
        slug={slug}
        logoUrl={PB_BRAND_LOGO_URL}
        logoAlt="Pure Bookkeeping — BKOS"
      >
        <h1 style={h1}>{firstName ? `You're in, ${firstName}.` : "You're in."} Welcome to BKOS.</h1>
        <p style={lede}>
          Your BKOS License is active. We're setting up your firm's seat on{' '}
          <strong>bkos.io</strong> now, and your login will arrive by email within one business day —
          often much sooner. That's where your Pure Bookkeeping templates, workflows, and systems live.
        </p>
        <div style={{ margin: '28px 0' }}>
          <a href={BKOS_LOGIN_URL} style={nextLink} target="_blank" rel="noopener noreferrer">
            Go to bkos.io →
          </a>
        </div>
        <p style={smallNote}>
          Order reference: <code style={code}>{order ?? '—'}</code>
          {email ? <> · receipt sent to <code style={code}>{email}</code></> : null}. Didn't get your
          login within one business day? Reply to your receipt email and we'll sort it.
        </p>
        {email && /^\S+@\S+\.\S+$/.test(email) && (
          <MarketingOptIn
            email={email}
            firstName={firstName || null}
            source="program-register"
            sourceDetail="bkos-license-thanks"
            programSlug="bkos-license"
            brandLabel="Pure Bookkeeping"
            style={{ marginTop: 20 }}
          />
        )}
      </PageChrome>
    );
  }

  // Freedom Gateway — 12-month PB-licensee program. Confirm payment, set the
  // schedule (weekly Thursday call with Teresa; intake kickoff with Lisa), and
  // list the pre-kickoff actions. (NA-correct for v1; a region-aware thanks page
  // is a follow-up once the AU cohort goes live — the Spiffy redirect drops the
  // region param, so region would need re-resolving by email here.)
  if (isFg) {
    return (
      <PageChrome eyebrow="Payment confirmed" slug={slug}>
        <h1 style={h1}>{greeting} Welcome to Freedom Gateway.</h1>
        <p style={lede}>
          Your seat is reserved and the Pure Bookkeeping System comes with it.{' '}
          {kickoff.kickoffDateTimeFull
            ? <>Your intake kicks off on <strong>{kickoff.kickoffDateTimeFull}</strong> — Lisa runs a newbie breakout at the start to get you oriented, then</>
            : 'Your intake kickoff date is in your welcome email — Lisa runs a newbie breakout at the start to get you oriented, then'}
          {' '}it's weekly group coaching with <strong>Teresa Slack every Thursday at 3pm ET</strong>.
        </p>
        <div style={{ margin: '28px 0' }}>
          <p style={{ ...lede, fontWeight: 600, marginBottom: '12px' }}>Before kickoff:</p>
          <ul style={{ ...lede, paddingLeft: '20px', display: 'grid', gap: '8px' }}>
            <li>Log in to PB Central and watch the Orientation.</li>
            <li>Open your Pixie account so it's ready for the system.</li>
            <li>Add the weekly Thursday 3pm ET call to your calendar.</li>
            <li>Review your Freedom Gateway workbook before week one.</li>
          </ul>
        </div>
        <p style={smallNote}>
          Order reference: <code style={code}>{order ?? '—'}</code>
          {email ? <> · receipt sent to <code style={code}>{email}</code></> : null}. We'll email your
          access details and the join link before kickoff. Anything off? Reply to your receipt email.
        </p>
        {email && /^\S+@\S+\.\S+$/.test(email) && (
          <MarketingOptIn
            email={email}
            firstName={firstName || null}
            source="program-register"
            sourceDetail="freedom-gateway-thanks"
            programSlug="freedom-gateway"
            brandLabel="The Successful Bookkeeper"
            style={{ marginTop: 20 }}
          />
        )}
      </PageChrome>
    );
  }

  return (
    <PageChrome eyebrow="Payment confirmed" slug={slug}>
      <h1 style={h1}>{greeting} Welcome to the Clients program.</h1>
      <p style={lede}>
        Your seat in {kickoff.cohortName ? <>the <strong>{kickoff.cohortName}</strong> intake</> : 'the next intake'} is reserved.{' '}
        {kickoff.kickoffDateTimeFull
          ? <>We kick off on <strong>{kickoff.kickoffDateTimeFull}</strong> with a newbie breakout — </>
          : ''}
        your first 90 days are instruction and implementation, then nine months of execution and
        support, a full year with your coaching team.
      </p>
      <NextSteps slug={slug} spiffy={spiffy} token={identity.token} kickoff={kickoff} />
      <p style={smallNote}>
        Order reference: <code style={code}>{order ?? '—'}</code>
        {email ? <> · sent to <code style={code}>{email}</code></> : null}. If
        anything looks off, reply to your welcome email and we'll sort it.
      </p>
      {email && /^\S+@\S+\.\S+$/.test(email) && (
        <MarketingOptIn
          email={email}
          firstName={firstName || null}
          source="program-register"
          sourceDetail="clients-thanks"
          programSlug="clients"
          brandLabel="The Successful Bookkeeper"
          style={{ marginTop: 20 }}
        />
      )}
    </PageChrome>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// /onboarding — short survey, then redirects to /summary
// ────────────────────────────────────────────────────────────────────────────

// The "90 days" question names a concrete calendar date on purpose (Lisa's
// framing — a real day is what makes the answer specific). It used to be a
// literal ("October 20" = Intake 2's July 22 kickoff + 90 days) and went stale
// the moment Clients went evergreen: membership now runs from each buyer's own
// purchase date, so the only date that is right for every buyer is 90 days
// from today. Computed at page load; never a literal.
function ninetyDaysFromNowLabel(): string {
  const d = new Date(Date.now() + 90 * 86_400_000);
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

const ONBOARDING_QUESTIONS: Array<{ id: string; label: string; placeholder: string; type: 'short' | 'long' }> = [
  {
    id: 'goal',
    label: 'What are you hoping to get out of the Clients program?',
    placeholder: "e.g. better clients, charge more confidently, build a referral pipeline that doesn't depend on me chasing it…",
    type: 'long',
  },
  {
    id: 'business_stage',
    label: 'Where is your bookkeeping business right now?',
    placeholder: 'e.g. solo + 8 clients, 2 years in, ~$60K/year, mostly word-of-mouth',
    type: 'short',
  },
  {
    id: 'biggest_block',
    label: 'What feels most stuck right now?',
    placeholder: 'e.g. pricing, finding the right clients, saying no to PITAs, marketing…',
    type: 'short',
  },
  {
    id: 'win_definition',
    label: `If the next 90 days went brilliantly, what would be different on ${ninetyDaysFromNowLabel()}?`,
    placeholder: 'No wrong answer — just what success looks like to you.',
    type: 'long',
  },
];

export function ProgramOnboardingPage() {
  const { slug = 'clients' } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const spiffy = readSpiffyParams(params);
  const identity = usePortalIdentity(slug, params);

  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = `Onboarding — Clients Program · ${BRAND_NAME}`;
    return () => {
      document.title = BRAND_NAME;
    };
  }, []);

  // Prefill existing answers on return visits once identity is resolved
  const prefillAttempted = useRef(false);
  useEffect(() => {
    if (identity.loading || prefillAttempted.current || !identity.token) return;
    prefillAttempted.current = true;
    fetch(`${API}/api/programs/${encodeURIComponent(slug)}/onboarding?token=${encodeURIComponent(identity.token)}`)
      .then((r) => r.json() as Promise<{ ok: boolean; answers: Record<string, string> | null }>)
      .then((data) => {
        if (data.ok && data.answers && Object.keys(data.answers).length > 0) {
          setAnswers(data.answers);
        }
      })
      .catch(() => null);
  }, [identity.loading, identity.token, slug]);

  // Resolved identity (token wins over Spiffy params)
  const firstName = identity.firstName ?? spiffy.firstName;
  const email = identity.email ?? spiffy.email;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/programs/${encodeURIComponent(slug)}/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: identity.token ?? null,
          order: spiffy.order,
          email,
          first_name: firstName,
          last_name: identity.lastName ?? spiffy.lastName,
          answers,
        }),
      });
      if (!res.ok && res.status !== 404) {
        setError('Couldn\'t save your answers, but you\'re still registered. Try again or skip ahead.');
      }
    } catch {
      setError('Couldn\'t save your answers, but you\'re still registered. Try again or skip ahead.');
    } finally {
      setSubmitting(false);
      setDone(true);
    }
  };

  // Show loading spinner while token is being verified
  if (identity.loading) {
    return (
      <PageChrome eyebrow="Onboarding · About your business" slug={slug}>
        <p style={lede}>Just a moment…</p>
      </PageChrome>
    );
  }

  // No identity at all — show email fallback form
  if (!email && !identity.loading) {
    return (
      <PageChrome eyebrow="Onboarding · About your business" slug={slug}>
        <h1 style={h1}>Let's find your account.</h1>
        <RequestAccessForm slug={slug} />
      </PageChrome>
    );
  }

  if (done) {
    const qs = buildStepQuery(spiffy, identity.token);
    return (
      <PageChrome eyebrow="Onboarding · Complete" slug={slug}>
        <h1 style={h1}>
          {firstName ? `Thanks, ${firstName}.` : 'Thanks.'} Lisa will see this before kickoff.
        </h1>
        <p style={lede}>That's everything we need from you for now. Your next step is on the program summary page.</p>
        <Link to={`/programs/${slug}/summary${qs}`} style={ctaBtn}>
          See what's next →
        </Link>
      </PageChrome>
    );
  }

  return (
    <PageChrome eyebrow="Onboarding · About your business" slug={slug}>
      <h1 style={h1}>
        {firstName ? `Tell Lisa a bit about you, ${firstName}.` : 'Tell Lisa a bit about you.'}
      </h1>
      <p style={lede}>
        Two minutes of context so Lisa can tailor the first session and the weekly coaching.
        Your answers go straight to her — no marketing, no automation, just so she knows who's
        in the room.
      </p>
      <form onSubmit={handleSubmit} style={form}>
        {ONBOARDING_QUESTIONS.map((q) => (
          <label key={q.id} style={fieldLabel}>
            <span style={fieldLabelText}>{q.label}</span>
            {q.type === 'long' ? (
              <textarea
                rows={4}
                placeholder={q.placeholder}
                value={answers[q.id] ?? ''}
                onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                style={textarea}
              />
            ) : (
              <input
                type="text"
                placeholder={q.placeholder}
                value={answers[q.id] ?? ''}
                onChange={(e) => setAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                style={input}
              />
            )}
          </label>
        ))}
        {error && <p style={errorText}>{error}</p>}
        <button type="submit" disabled={submitting} style={ctaBtn}>
          {submitting ? 'Saving…' : 'Send to Lisa →'}
        </button>
        <p style={smallNote}>
          You can edit your answers any time before kickoff — just come back to this page.
        </p>
      </form>
    </PageChrome>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// /summary — program summary the buyer lands on after onboarding
// ────────────────────────────────────────────────────────────────────────────

export function ProgramSummaryPage() {
  const { slug = 'clients' } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const spiffy = readSpiffyParams(params);
  const identity = usePortalIdentity(slug, params);
  const email = identity.email ?? spiffy.email;
  // See useKickoffForEmail's doc comment — resolves THIS buyer's own cohort,
  // not the program's active one, so an Intake 2 member sees Intake 2's
  // schedule even after Intake 3 becomes the active cohort.
  const kickoff = useKickoffForEmail(slug, email);

  useEffect(() => {
    document.title = `Your program · Clients · ${BRAND_NAME}`;
    return () => {
      document.title = BRAND_NAME;
    };
  }, []);

  const firstName = identity.firstName ?? spiffy.firstName;

  return (
    <PageChrome
      eyebrow={`You're registered · Clients Program${kickoff.cohortName ? ` · ${kickoff.cohortName}` : ''}`}
      slug={slug}
    >
      <h1 style={h1}>
        {firstName
          ? `Here's everything you need to know, ${firstName}.`
          : "Here's everything you need to know."}
      </h1>
      <p style={lede}>
        Your seat is locked in. Below is the schedule, the calls you have access to, and where to
        show up when. Bookmark this page — it's your one-stop reference.
      </p>

      <section style={summaryBlock}>
        <h2 style={h2}>Live weekly coaching with Lisa, Teresa &amp; Jennifer</h2>
        <p style={summaryBody}>
          Every <strong>Wednesday, 11am–12pm ET</strong>
          {kickoff.kickoffDateLongYear ? <>, starting <strong>{kickoff.kickoffDateLongYear}</strong></> : ''} —
          your Clients coaching team (Teresa and Jennifer week to week, Lisa often on the call), with a
          newbie breakout at the start of your intake. The weekly calls run the full twelve months.
          The Komunily welcome email contains the recurring Zoom link.
        </p>
      </section>

      <section style={summaryBlock}>
        <h2 style={h2}>Monthly community calls (you have access to both)</h2>
        <ul style={bulletList}>
          <li>
            <strong>Monthly Sales Success</strong> with Debra Angilletta — 2nd Wednesday of every
            month, 1–2pm ET.
          </li>
          <li>
            <strong>Monthly Motivational Mindset</strong> with Steve Kushman — 3rd Friday of
            every month, 1–2pm ET.
          </li>
        </ul>
      </section>

      <section style={summaryBlock}>
        <h2 style={h2}>Module resources</h2>
        <p style={summaryBody}>
          Course materials drip per module as the program unfolds — you'll get an email when each
          new module unlocks, plus a link to it in Komunily.
        </p>
      </section>

      <section style={summaryBlock}>
        <h2 style={h2}>Need to reach us?</h2>
        <p style={summaryBody}>
          Reply to any email from Lisa, or hit us in the Clients channel inside Komunily. We see
          everything.
        </p>
      </section>

      <p style={smallNote}>
        Order reference: <code style={code}>{spiffy.order ?? '—'}</code>
      </p>
    </PageChrome>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Inline styles
// ────────────────────────────────────────────────────────────────────────────

const PROG_PRIMARY = 'var(--prog-primary, #FF6B35)';
const PROG_TEXT_DARK = 'var(--prog-text-dark, #0A0A2E)';
const PROG_TINT = 'var(--prog-tint, #FFF3EE)';
const PROG_CANVAS = 'var(--prog-canvas, #F8F6F3)';
const FONT_HEADING = 'var(--font-heading, "Fraunces", Georgia, serif)';
const FONT_BODY = 'var(--font-body, "Poppins", -apple-system, sans-serif)';

const pageWrap: React.CSSProperties = {
  minHeight: '100vh',
  background: PROG_CANVAS,
  color: PROG_TEXT_DARK,
  fontFamily: FONT_BODY,
};

const topNav: React.CSSProperties = {
  padding: '12px 24px',
  borderBottom: '1px solid rgba(10,10,46,0.08)',
  background: '#fff',
};
const topNavInner: React.CSSProperties = {
  maxWidth: '1140px',
  margin: '0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
};
const topNavLogoLink: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
};
const topNavLogo: React.CSSProperties = {
  height: '36px',
  width: 'auto',
  display: 'block',
};
const topNavLink: React.CSSProperties = {
  fontFamily: FONT_BODY,
  fontSize: '13px',
  fontWeight: 600,
  color: PROG_PRIMARY,
  textDecoration: 'none',
  borderBottom: `2px solid ${PROG_PRIMARY}`,
  paddingBottom: '1px',
};

const mainWrap: React.CSSProperties = {
  maxWidth: '880px',
  margin: '0 auto',
  padding: '56px 24px 88px',
};

const eyebrow: React.CSSProperties = {
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: PROG_PRIMARY,
  margin: '0 0 14px',
};

const h1: React.CSSProperties = {
  fontFamily: FONT_HEADING,
  fontSize: 'clamp(30px, 4.2vw, 40px)',
  fontWeight: 600,
  lineHeight: 1.15,
  margin: '0 0 16px',
};

const h2: React.CSSProperties = {
  fontFamily: FONT_HEADING,
  fontSize: '22px',
  fontWeight: 600,
  margin: '0 0 10px',
};

const lede: React.CSSProperties = {
  fontSize: '17px',
  lineHeight: 1.6,
  color: PROG_TEXT_DARK,
  opacity: 0.85,
  margin: '0 0 28px',
};

const summaryBlock: React.CSSProperties = {
  background: '#fff',
  borderRadius: 'var(--radius-card, 10px)',
  padding: '22px 24px',
  marginBottom: '14px',
  border: '1px solid rgba(10,10,46,0.08)',
};

const summaryBody: React.CSSProperties = {
  fontSize: '15px',
  lineHeight: 1.6,
  color: PROG_TEXT_DARK,
  opacity: 0.85,
  margin: 0,
};

const bulletList: React.CSSProperties = {
  margin: '8px 0 0 18px',
  padding: 0,
  fontSize: '15px',
  lineHeight: 1.7,
  color: PROG_TEXT_DARK,
};

const nextStepsCard: React.CSSProperties = {
  background: PROG_TINT,
  borderRadius: 'var(--radius-card, 10px)',
  padding: '24px 28px',
  marginBottom: '24px',
  borderLeft: `4px solid ${PROG_PRIMARY}`,
};

const nextStepsHead: React.CSSProperties = {
  fontFamily: FONT_HEADING,
  fontSize: '18px',
  fontWeight: 600,
  margin: '0 0 12px',
};

const nextStepsList: React.CSSProperties = {
  margin: '0 0 0 20px',
  padding: 0,
  fontSize: '15px',
  lineHeight: 1.65,
  color: PROG_TEXT_DARK,
};

const nextLink: React.CSSProperties = {
  display: 'inline-block',
  color: PROG_PRIMARY,
  fontWeight: 700,
  textDecoration: 'none',
  borderBottom: `2px solid ${PROG_PRIMARY}`,
  paddingBottom: '1px',
};

const ctaBtn: React.CSSProperties = {
  display: 'inline-block',
  background: PROG_PRIMARY,
  color: '#fff',
  fontFamily: FONT_BODY,
  fontSize: '16px',
  fontWeight: 700,
  padding: '14px 26px',
  border: 'none',
  borderRadius: 'var(--radius-button, 6px)',
  textDecoration: 'none',
  cursor: 'pointer',
  marginTop: '10px',
};

const form: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '20px',
  marginTop: '8px',
};

const fieldLabel: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const fieldLabelText: React.CSSProperties = {
  fontSize: '15px',
  fontWeight: 600,
  color: PROG_TEXT_DARK,
};

const input: React.CSSProperties = {
  padding: '11px 13px',
  fontSize: '15px',
  border: '1px solid #D1D1D6',
  borderRadius: '6px',
  fontFamily: FONT_BODY,
  color: PROG_TEXT_DARK,
};

const textarea: React.CSSProperties = {
  padding: '11px 13px',
  fontSize: '15px',
  border: '1px solid #D1D1D6',
  borderRadius: '6px',
  fontFamily: FONT_BODY,
  color: PROG_TEXT_DARK,
  resize: 'vertical',
};

const errorText: React.CSSProperties = {
  color: '#B91C1C',
  fontSize: '13px',
  margin: 0,
};

const smallNote: React.CSSProperties = {
  fontSize: '12px',
  color: PROG_TEXT_DARK,
  opacity: 0.6,
  marginTop: '24px',
};

const code: React.CSSProperties = {
  fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
  fontSize: '12px',
  background: 'rgba(10,10,46,0.05)',
  padding: '2px 6px',
  borderRadius: '4px',
};

const meetingEmbedWrap: React.CSSProperties = {
  background: '#fff',
  borderRadius: 'var(--radius-card, 10px)',
  border: '1px solid rgba(10,10,46,0.08)',
  overflow: 'hidden',
  marginBottom: '24px',
};

const meetingIframe: React.CSSProperties = {
  display: 'block',
  width: '100%',
  height: '720px',
  border: 'none',
};
